import { useState } from 'react';
import { Plus, Trash2, Save, X, Loader2, Undo2 } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import type { ActualBillResult, ActualBillLine, ShadowBillSection } from '../../types/billTypes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

// A cleared Qty/Rate becomes undefined, not 0 — an unknown rate must stay unknown
// rather than read as "zero". Mirrors ExpectedInvoiceEditor's numOrUndef.
const numOrUndef = (raw: string): number | undefined =>
  raw === '' ? undefined : (Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : undefined);

// GST here is taken from the GST lines exactly as entered — deleting the GST line
// yields zero GST, NOT 10% of the subtotal. This edits a PARSED invoice, whose GST
// was read off the bill; an invented 10% would contradict a bill whose charges are
// GST-free. (Contrast utils/shadowOverride, which predicts a bill and keeps a 10%
// fallback when no gst line exists.)
export function recalc(lines: ActualBillLine[], baseResult: ActualBillResult): ActualBillResult {
  const section_totals: Record<ShadowBillSection, number> = {
    retail: 0, distribution: 0, metering: 0, environmental: 0, other: 0, gst: 0,
  };
  for (const l of lines) {
    if (l.section in section_totals) {
      section_totals[l.section] = parseFloat(((section_totals[l.section] ?? 0) + (l.amount || 0)).toFixed(2));
    }
  }
  const subtotal = parseFloat(
    (section_totals.retail + section_totals.distribution + section_totals.metering
      + section_totals.environmental + section_totals.other).toFixed(2),
  );
  const gst = section_totals.gst;
  const total = parseFloat((subtotal + gst).toFixed(2));
  return { ...baseResult, lines, section_totals, subtotal, gst, total };
}

// ─── Section config ───────────────────────────────────────────────────────────

const EDITABLE_SECTIONS: { key: ShadowBillSection; label: string }[] = [
  { key: 'retail',        label: 'Retail Energy Charges' },
  { key: 'distribution',  label: 'Network / Distribution Charges' },
  { key: 'metering',      label: 'Metering Charges' },
  { key: 'environmental', label: 'Environmental Charges' },
  { key: 'other',         label: 'Other Charges' },
  { key: 'gst',           label: 'GST' },
];

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  initialResult: ActualBillResult;
  /**
   * What the parser originally read, when `initialResult` is a corrected copy.
   * Used only to show "was 206.29" beside a changed amount — corrections are
   * stored separately from the parse, so both are always available.
   */
  parsedResult?: ActualBillResult | null;
  isEdited?: boolean;
  onSave: (result: ActualBillResult, note: string) => Promise<void>;
  /** Drops every correction and returns to the parse. Hidden when nothing is edited. */
  onRevert?: () => Promise<void>;
  onCancel: () => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

export function ParsedInvoiceEditor({
  initialResult, parsedResult, isEdited, onSave, onRevert, onCancel,
}: Props) {
  const [reverting, setReverting] = useState(false);
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<ActualBillLine[]>(() => [...initialResult.lines]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const computed = recalc(lines, initialResult);

  const updateLine = (idx: number, patch: Partial<ActualBillLine>) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const deleteLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const addLine = (section: ShadowBillSection) => {
    setLines((prev) => [...prev, { section, description: '', amount: 0 }]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(computed, note);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/30 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-blue-200">
        <div>
          <p className="text-sm font-semibold text-gray-900">Edit Invoice Values</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {isEdited
              ? 'Showing your corrections. The original parse is kept and can be restored.'
              : 'Correct any values the AI parsed incorrectly, then save.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Only offered once something has actually been corrected — there is
              nothing to revert to otherwise, and the parse is never overwritten. */}
          {isEdited && onRevert && (
            <Button
              variant="outline"
              size="sm"
              disabled={saving || reverting}
              className="gap-1.5 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                if (!window.confirm(
                  'Discard every correction and go back to what the parser read?\n\n'
                  + 'The corrected figures will be lost. The parse itself was never changed.',
                )) return;
                setReverting(true);
                try { await onRevert(); } finally { setReverting(false); }
              }}
            >
              {reverting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
              Revert to parsed
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex items-center gap-1.5">
          <X className="h-3.5 w-3.5 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Suggestions only — a retailer may quote something not on this list, so the
          field stays free text. */}
      <datalist id="bv-rate-units">
        <option value="¢/kWh" />
        <option value="$/kWh" />
        <option value="¢/kVA/day" />
        <option value="$/kVA/year" />
        <option value="$/day" />
        <option value="$/meter/day" />
        <option value="$/meter/month" />
        <option value="$/meter/year" />
        <option value="$/servicePoint/day" />
        <option value="$/servicePoint/month" />
        <option value="$/servicePoint/year" />
      </datalist>

      {/* Optional, on purpose. Blocking the save until someone types something
          produces "fix" and "correction"; leaving it optional means the notes
          that do get written are the ones worth reading. Shown on the
          parsed-vs-corrected comparison, where the question "why is this
          different from the PDF" actually gets asked. */}
      <div className="px-4 py-3 bg-white border-b border-blue-200">
        <label htmlFor="edit-note" className="text-xs font-medium text-gray-600">
          Why are you changing this? <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          id="edit-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={saving}
          placeholder="e.g. metering charge is 31 days × $2.38356 — the parser read the quantity as 1 meter"
          className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          Shown beside the parsed and corrected figures, so anyone reviewing the claim can see why.
        </p>
      </div>

      {/* Sections */}
      <div className="divide-y divide-gray-200">
        {EDITABLE_SECTIONS.map(({ key, label }) => {
          const sectionLines = lines.map((l, idx) => ({ l, idx })).filter(({ l }) => l.section === key);
          const sectionTotal = computed.section_totals[key] ?? 0;

          return (
            <div key={key} className="bg-white">
              {/* Section header */}
              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 tabular-nums">
                    Total: <span className="font-semibold text-gray-800">{fmt(sectionTotal)}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2"
                    onClick={() => addLine(key)}
                  >
                    <Plus className="h-3 w-3" /> Add Line
                  </Button>
                </div>
              </div>

              {/* Lines */}
              {sectionLines.length > 0 ? (
                <div className="px-4 py-2 space-y-1.5">
                  {sectionLines.map(({ l, idx }) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={l.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        placeholder="Description"
                        className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {/* Qty, Unit and Rate record how the amount was reached. They are
                          NOT multiplied into it — rate units differ per line (c/kWh vs
                          dollars) and the amount is entered directly. Same decision as
                          ExpectedInvoiceEditor / utils/shadowOverride. */}
                      <input
                        type="number"
                        step="any"
                        value={l.volume ?? ''}
                        onChange={(e) => updateLine(idx, { volume: numOrUndef(e.target.value) })}
                        placeholder="Qty"
                        title="Quantity"
                        className="w-24 px-2 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={l.volume_unit ?? ''}
                        onChange={(e) => updateLine(idx, { volume_unit: e.target.value || undefined })}
                        placeholder="Unit"
                        title="QTY unit — meters, servicePoint, kVA"
                        className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {/* Usage sits beside QTY rather than replacing it — a
                          retailer's metering line quotes $/meter/day across 31
                          days, so the count and the period are both on the page. */}
                      <input
                        type="number"
                        step="any"
                        value={l.usage ?? ''}
                        onChange={(e) => updateLine(idx, { usage: numOrUndef(e.target.value) ?? null })}
                        placeholder="Usage"
                        title="Usage — kWh billed, or days the charge ran over"
                        className="w-24 px-2 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={l.usage_unit ?? ''}
                        onChange={(e) => updateLine(idx, { usage_unit: e.target.value || null })}
                        placeholder="Unit"
                        title="Usage unit — kWh or days"
                        className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="number"
                        step="any"
                        value={l.rate ?? ''}
                        onChange={(e) => updateLine(idx, { rate: numOrUndef(e.target.value) })}
                        placeholder="Rate"
                        title="Rate"
                        className="w-24 px-2 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {/* Job 20: the unit the retailer quotes the rate in. The parser
                          does not read it, and without it the comparison shows a
                          contracted $/meter/year beside a billed $/meter/day as two
                          bare numbers. Recording it here makes the mismatch visible. */}
                      <input
                        type="text"
                        list="bv-rate-units"
                        value={l.rate_unit ?? ''}
                        onChange={(e) => updateLine(idx, { rate_unit: e.target.value || undefined })}
                        placeholder="Rate unit"
                        title="How the retailer quotes this rate — e.g. ¢/kWh, $/meter/day, $/kVA/year"
                        className="w-32 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="relative flex-shrink-0">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={l.amount}
                          title={parsedResult?.lines?.[idx]
                            && Math.abs((parsedResult.lines[idx].amount ?? 0) - (l.amount ?? 0)) > 0.005
                            ? `The parser read ${parsedResult.lines[idx].amount}`
                            : 'Amount'}
                          onChange={(e) => updateLine(idx, { amount: parseFloat(e.target.value) || 0 })}
                          className={cn(
                            'w-28 pl-6 pr-2.5 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent tabular-nums',
                            l.amount < 0 && 'text-green-700',
                          )}
                        />
                      </div>
                      <button
                        onClick={() => deleteLine(idx)}
                        title="Remove line"
                        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-2 text-xs text-gray-400 italic">No lines — click Add Line to add one</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Live totals footer */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
        <div className="max-w-xs ml-auto space-y-1.5">
          {(['retail', 'distribution', 'metering', 'environmental', 'other'] as ShadowBillSection[]).map((s) => {
            const v = computed.section_totals[s];
            if (!v) return null;
            return (
              <div key={s} className="flex justify-between text-xs text-gray-600">
                <span className="capitalize">{s}</span>
                <span className="tabular-nums">{fmt(v)}</span>
              </div>
            );
          })}
          <div className="flex justify-between text-sm pt-1.5 border-t border-gray-300">
            <span className="text-gray-600">Subtotal (excl. GST)</span>
            <span className="tabular-nums font-medium">{fmt(computed.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">GST</span>
            <span className="tabular-nums text-gray-600">{fmt(computed.gst)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold pt-1.5 border-t-2 border-gray-900">
            <span>TOTAL</span>
            <span className="tabular-nums text-orange-800">{fmt(computed.total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
